import { useCallback, useEffect, useRef, useState } from 'react';
import type { CoachLeaderNotesScope } from '../../../../services/api/coachSessionPrep';

/**
 * MED-1081 — debounced autosave for the shared Leader Notes editor.
 *
 * Brings Leader Notes in line with the product's standard autosave family
 * (`useReflectionAutosave`, `useFeedbackAutosave`): note text persists
 * automatically as the facilitator types, so it can no longer be lost by
 * clicking "Mark as reviewed" or navigating away. Replaces the old explicit
 * "Save" link.
 *
 * The hook is persistence-agnostic — it calls the `onSave` callback supplied by
 * each consumer (coach prep vs peer-leader prep persist via different APIs), so
 * one editor change fixes both pages.
 */

export type LeaderNotesAutosaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface LeaderNotesSaveArgs {
  blockKey: string;
  scope: CoachLeaderNotesScope;
  groupId: string | null;
  contentHtml: string;
}

const DEFAULT_DEBOUNCE_MS = 800;

export function useLeaderNotesAutosave(
  onSave: (args: LeaderNotesSaveArgs) => Promise<void>,
  debounceMs: number = DEFAULT_DEBOUNCE_MS,
) {
  const [state, setState] = useState<LeaderNotesAutosaveState>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<LeaderNotesSaveArgs | null>(null);
  // Keep the latest onSave without re-arming the debounce every render — the
  // consumer's callback identity changes across renders, but the in-flight
  // debounce timer should still call the freshest version.
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const performSave = useCallback(async () => {
    const args = pendingRef.current;
    if (!args) return;
    pendingRef.current = null;
    setState('saving');
    try {
      await onSaveRef.current(args);
      setState('saved');
    } catch {
      // Re-queue the failed text so the next keystroke or flush retries it —
      // but never clobber a newer pending edit captured during the await.
      if (!pendingRef.current) pendingRef.current = args;
      setState('error');
    }
  }, []);

  const scheduleSave = useCallback(
    (args: LeaderNotesSaveArgs) => {
      pendingRef.current = args;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void performSave();
      }, debounceMs);
    },
    [debounceMs, performSave],
  );

  /** Cancel the debounce and persist any pending text immediately. */
  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await performSave();
  }, [performSave]);

  // Navigate-away safety: persist pending text on unmount. Cleanup can't await,
  // but the PUT is still dispatched (fire-and-forget).
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (pendingRef.current) void performSave();
    };
  }, [performSave]);

  return { state, scheduleSave, flush };
}
